/* eslint-disable max-lines */
import { observable } from 'mobx';
import _ from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';
import type { TransportElementTerminalResponseArgs } from './transport_element_terminal/transport_element_terminal2';
import TransportElementTerminal, {
  convertTransportElementTerminalResponseToArgs
} from './transport_element_terminal/transport_element_terminal2';
import { toDomesticTel } from '../util';
import type OrderItem from './order_item2';
import type ElementDetailOptions from './element_detail_options';
import type { TransportElementAttachFileResponseArgs } from './transport_element_attach_file2';
import TransportElementAttachFile, {
  convertTransportElementAttachFileResponseToArgs
} from './transport_element_attach_file2';
import type { TransportElementReservationResponseArgs } from './transport_element_reservation2';
import TransportElementReservation, {
  convertTransportElementReservationResponseToArgs
} from './transport_element_reservation2';
import type { TicketOprReservationResponseArgs } from './ticket_opr_reservation2';
import TicketOprReservation, { convertTicketOprReservationResponseToArgs } from './ticket_opr_reservation2';
import type SuppliedItem from './supplied_item/supplied_item2';
import type { IndividualDomesticAirSuppliedItem } from './individual_domestic_air_supplied_item';

export type TransportType =
  | 'domestic_air'
  | 'foreign_air'
  | 'shinkansen'
  | 'express'
  | 'railway_ticket'
  | 'rental_car'
  | 'bus'
  | 'ana_tabisaku'
  | 'taxi'
  | 'foreign_railway'
  | 'other';

export type UploadFileData = {
  upload_file_name: string;
  content_type: string;
  traveler_information_id: string | null;
  upload_file_base64: string;
  // TransportElementAttachFileから変換するときだけ下記が必要
  id?: number;
  trip_id?: number;
  transport_element_id?: number;
  file_path?: string;
};

export type TransportElementResponseArgs = {
  id: number;
  name: string;
  transport_type: TransportType;
  from: TransportElementTerminalResponseArgs;
  to: TransportElementTerminalResponseArgs;
  air_reservation_number: string | null;
  air_confirmation_number: string | null;
  reservation_number: string | null;
  air_seat_number?: string;
  car_provider_text?: string;
  car_type_text?: string;
  last_name_kana?: string;
  first_name_kana?: string;
  driver_tel?: string;
  remarks?: string;
  ticketing_expired_at?: string;
  air_ticket_type_str?: string;
  air_seat_type_str?: string;
  air_seat_type?: string;
  cabin: string | null;
  boarding_station?: string;
  arrival_station?: string;
  air_ticket_type?: number;
  marketlog_available?: boolean;
  railway_seat_type: number | null;
  railway_ticket_type: number | null;
  transport_element_attach_files?: TransportElementAttachFileResponseArgs[];
  transport_element_reservations?: TransportElementReservationResponseArgs[];
  ticket_opr_reservations?: TicketOprReservationResponseArgs[];
  sold_out?: boolean;
  people_num?: number;
  ticketing_type?: number;
  posting_number: string | null;
  exic_receipt_number: string | null;
  auto_reservation_status: string | null;
  airline_code?: string;
  car_type?: string;
  car_type_other?: string;
  car_number?: string;
  car_provider?: string;
  taxi_service?: string;
  ticketing_at?: string;
  ticket_number: string | null;
  sakura_ticket_type?: string;
  air_changeable_description_with_fare?: string;
  allies_application_number?: string;
  frontier_application_number?: string;
  skytours_application_number?: string;
  detail_path?: string;
};

export type TransportElementArgs = {
  id?: number;
  name?: string;
  transportType: TransportType;
  from: TransportElementTerminal;
  to: TransportElementTerminal;
  airReservationNumber?: string | null;
  airConfirmationNumber?: string | null;
  reservationNumber?: string | null;
  airSeatNumber?: string;
  carProviderText?: string;
  carTypeText?: string;
  lastNameKana?: string;
  firstNameKana?: string;
  driverTel?: string;
  remarks?: string;
  ticketingExpiredAt?: Moment;
  airTicketTypeStr?: string;
  airSeatType?: string;
  cabin?: string | null;
  boardingStation?: string;
  arrivalStation?: string;
  airTicketType?: number;
  marketlogAvailable?: boolean;
  railwaySeatType?: number | null;
  railwayTicketType?: number | null;
  transportElementAttachFiles?: TransportElementAttachFile[];
  transportElementReservations?: TransportElementReservation[];
  ticketOprReservations?: TicketOprReservation[];
  soldOut?: boolean;
  peopleNum?: number;
  ticketingType?: number;
  postingNumber?: string | null;
  exicReceiptNumber?: string | null;
  autoReservationStatus?: string | null;
  airlineCode?: string;
  carType?: string;
  carTypeOther?: string;
  carNumber?: string;
  carProvider?: string;
  taxiService?: string;
  ticketingAt?: Moment;
  ticketNumber?: string | null;
  sakuraTicketType?: string;
  airChangeableDescriptionWithFare?: string;
  alliesApplicationNumber?: string;
  frontierApplicationNumber?: string;
  skytoursApplicationNumber?: string;
  detailPath?: string;
};

export const convertTransportElementResponseToArgs = (response: TransportElementResponseArgs) => {
  const args: TransportElementArgs = {
    id: response.id,
    name: response.name,
    transportType: response.transport_type,
    from: new TransportElementTerminal(convertTransportElementTerminalResponseToArgs(response.from)),
    to: new TransportElementTerminal(convertTransportElementTerminalResponseToArgs(response.to)),
    airReservationNumber: response.air_reservation_number,
    airConfirmationNumber: response.air_confirmation_number,
    reservationNumber: response.reservation_number,
    airSeatNumber: response.air_seat_number,
    carProviderText: response.car_provider_text,
    carTypeText: response.car_type_text,
    lastNameKana: response.last_name_kana,
    firstNameKana: response.first_name_kana,
    driverTel: response.driver_tel,
    remarks: response.remarks,
    ticketingExpiredAt: response.ticketing_expired_at ? moment(response.ticketing_expired_at) : undefined,
    airTicketTypeStr: response.air_ticket_type_str,
    airSeatType: response.air_seat_type,
    cabin: response.cabin,
    boardingStation: response.boarding_station,
    arrivalStation: response.arrival_station,
    airTicketType: response.air_ticket_type,
    marketlogAvailable: response.marketlog_available,
    railwaySeatType: response.railway_seat_type,
    railwayTicketType: response.railway_ticket_type,
    transportElementAttachFiles: response.transport_element_attach_files
      ? response.transport_element_attach_files.map(
          file =>
            new TransportElementAttachFile({
              ...convertTransportElementAttachFileResponseToArgs(file),
              transportElementId: response.id
            })
        )
      : undefined,
    transportElementReservations: response.transport_element_reservations
      ? response.transport_element_reservations.map(
          reservation =>
            new TransportElementReservation(convertTransportElementReservationResponseToArgs(reservation))
        )
      : undefined,
    ticketOprReservations: response.ticket_opr_reservations
      ? response.ticket_opr_reservations.map(
          reservation => new TicketOprReservation(convertTicketOprReservationResponseToArgs(reservation))
        )
      : undefined,
    soldOut: response.sold_out,
    peopleNum: response.people_num,
    ticketingType: response.ticketing_type,
    postingNumber: response.posting_number,
    exicReceiptNumber: response.exic_receipt_number,
    autoReservationStatus: response.auto_reservation_status,
    airlineCode: response.airline_code,
    carType: response.car_type,
    carTypeOther: response.car_type_other,
    carNumber: response.car_number,
    carProvider: response.car_provider,
    taxiService: response.taxi_service,
    ticketingAt: response.ticketing_at ? moment(response.ticketing_at) : undefined,
    ticketNumber: response.ticket_number,
    sakuraTicketType: response.sakura_ticket_type,
    airChangeableDescriptionWithFare: response.air_changeable_description_with_fare,
    alliesApplicationNumber: response.allies_application_number,
    frontierApplicationNumber: response.frontier_application_number,
    skytoursApplicationNumber: response.skytours_application_number,
    detailPath: response.detail_path
  };
  return args;
};

export default class TransportElement {
  static readonly TRANSPORT_TYPES = {
    DOMESTIC_AIR: 'domestic_air' as TransportType,
    FOREIGN_AIR: 'foreign_air' as TransportType
  };

  @observable id?: number;
  @observable name?: string;
  @observable transportType: TransportType;
  @observable from: TransportElementTerminal;
  @observable to: TransportElementTerminal;
  @observable airReservationNumber?: string | null;
  @observable airConfirmationNumber?: string | null;
  @observable reservationNumber?: string | null;
  @observable airSeatNumber?: string;
  @observable carProviderText?: string;
  @observable carTypeText?: string;
  @observable lastNameKana?: string;
  @observable firstNameKana?: string;
  @observable driverTel?: string;
  @observable remarks?: string;
  @observable ticketingExpiredAt?: Moment;
  @observable airTicketTypeStr?: string;
  @observable airSeatType?: string;
  @observable cabin?: string | null;
  @observable boardingStation?: string;
  @observable arrivalStation?: string;
  @observable airTicketType?: number;
  @observable marketlogAvailable?: boolean;
  @observable railwaySeatType?: number | null;
  @observable railwayTicketType?: number | null;
  @observable transportElementAttachFiles: TransportElementAttachFile[];
  @observable transportElementReservations?: TransportElementReservation[];
  @observable ticketOprReservations?: TicketOprReservation[];
  @observable soldOut: boolean;
  @observable peopleNum?: number;
  @observable ticketingType?: number;
  @observable postingNumber?: string | null;
  @observable exicReceiptNumber?: string | null;
  @observable autoReservationStatus?: string | null;
  @observable airlineCode?: string;
  @observable carType?: string;
  @observable carTypeOther?: string;
  @observable carNumber?: string;
  @observable carProvider?: string;
  @observable taxiService?: string;
  @observable ticketingAt?: Moment;
  @observable ticketNumber?: string | null;
  @observable uploadFileData?: Record<number, Record<string, UploadFileData[]>>;
  @observable sakuraTicketType?: string;
  @observable airChangeableDescriptionWithFare?: string;
  @observable alliesApplicationNumber?: string;
  @observable frontierApplicationNumber?: string;
  @observable skytoursApplicationNumber?: string;
  @observable detailPath?: string;

  constructor(args: TransportElementArgs) {
    this.id = args.id;
    this.name = args.name;
    this.transportType = args.transportType;
    this.from = args.from;
    this.to = args.to;
    this.airReservationNumber = args.airReservationNumber;
    this.airConfirmationNumber = args.airConfirmationNumber;
    this.reservationNumber = args.reservationNumber;
    this.airSeatNumber = args.airSeatNumber;
    this.carProviderText = args.carProviderText;
    this.carTypeText = args.carTypeText;
    this.lastNameKana = args.lastNameKana;
    this.firstNameKana = args.firstNameKana;
    this.driverTel = args.driverTel;
    this.remarks = args.remarks;
    this.ticketingExpiredAt = args.ticketingExpiredAt;
    this.airTicketTypeStr = args.airTicketTypeStr;
    this.airSeatType = args.airSeatType;
    this.cabin = args.cabin;
    this.boardingStation = args.boardingStation;
    this.arrivalStation = args.arrivalStation;
    this.airTicketType = args.airTicketType;
    this.marketlogAvailable = args.marketlogAvailable;
    this.railwaySeatType = args.railwaySeatType;
    this.railwayTicketType = args.railwayTicketType;
    this.transportElementAttachFiles = args.transportElementAttachFiles || [];
    this.transportElementReservations = args.transportElementReservations;
    this.ticketOprReservations = args.ticketOprReservations;
    this.soldOut = args.soldOut || false;
    this.ticketingType = args.ticketingType;
    this.postingNumber = args.postingNumber;
    this.exicReceiptNumber = args.exicReceiptNumber;
    this.autoReservationStatus = args.autoReservationStatus;
    this.airlineCode = args.airlineCode;
    this.carType = args.carType;
    this.carTypeOther = args.carTypeOther;
    this.carNumber = args.carNumber;
    this.carProvider = args.carProvider;
    this.taxiService = args.taxiService;
    this.ticketingAt = args.ticketingAt;
    this.ticketNumber = args.ticketNumber;
    this.sakuraTicketType = args.sakuraTicketType;
    this.airChangeableDescriptionWithFare = args.airChangeableDescriptionWithFare;
    this.alliesApplicationNumber = args.alliesApplicationNumber;
    this.frontierApplicationNumber = args.frontierApplicationNumber;
    this.skytoursApplicationNumber = args.skytoursApplicationNumber;
    this.peopleNum = args.peopleNum;
    this.detailPath = args.detailPath;

    // transportElementAttachFilesからuploadFileDataを初期化
    const initialUploadData = this.buildUploadFileDataFromAttachFiles(this.transportElementAttachFiles);
    this.uploadFileData = initialUploadData ?? {};
  }

  get type() {
    return 'transport';
  }

  get summary() {
    let s = '';
    if (this.transportType === 'foreign_air') {
      s = `${this.from.foreignAirDescription}発 → ${this.to.foreignAirDescription}着 ${this.name}`;
    } else {
      s = `${this.from.description}発 → ${this.to.description}着`;
      if (this.name) {
        s += ` ${this.name.replace(/\n/g, ' ')}`;
      }
    }
    if (this.airReservationNumber && this.airConfirmationNumber) {
      s += `\n予約番号:${this.airReservationNumber}`;
      s += `\n確認番号:${this.airConfirmationNumber}`;
    }
    if (this.carProviderText) {
      s += `\n提供元:${this.carProviderText}`;
    }
    if (this.carTypeText) {
      s += `\n車種・クラス:${this.carTypeText}`;
    }
    if (this.lastNameKana && this.firstNameKana) {
      s += `\n運転者氏名（カナ）:${this.lastNameKana} ${this.firstNameKana}`;
    }
    if (this.driverTel) {
      s += `\n運転者電話番号:${toDomesticTel(this.driverTel)}`;
    }
    if (this.remarks) {
      s += `\n備考:${this.remarks}`;
    }
    return s;
  }

  get title() {
    switch (this.transportType) {
      case 'domestic_air':
        return '国内航空券';
      case 'foreign_air':
        return '海外航空券';
      case 'shinkansen':
        return '新幹線';
      case 'express':
        return '特急';
      case 'railway_ticket':
        return '在来線';
      case 'foreign_railway':
        return '海外鉄道';
      case 'rental_car':
        return 'レンタカー';
      case 'bus':
        return 'バス';
      case 'ana_tabisaku':
        return 'ANA旅作';
      case 'taxi':
        return 'タクシー';
      case 'other':
        return 'その他';
      default:
        return '';
    }
  }

  get isRailway() {
    return (
      this.transportType === 'shinkansen' ||
      this.transportType === 'express' ||
      this.transportType === 'railway_ticket' ||
      this.transportType === 'foreign_railway'
    );
  }

  get isShinkansen() {
    return this.transportType === 'shinkansen';
  }

  get isExpress() {
    return this.transportType === 'express';
  }

  get isRailwayTicket() {
    return this.transportType === 'railway_ticket';
  }

  get isForeignRailway() {
    return this.transportType === 'foreign_railway';
  }

  get isSelectBulkTicket() {
    return this.railwayTicketType !== 2;
  }

  get isAir() {
    return this.transportType === 'domestic_air' || this.transportType === 'foreign_air';
  }

  get isDomesticAir() {
    return this.transportType === 'domestic_air';
  }

  get isForeignAir() {
    return this.transportType === 'foreign_air';
  }

  get mappingDescription(): string {
    const timeStr = this.from?.time ? this.from.time.tz(this.from.timeZone).format('HH:mm') : '';
    const name = this.carTypeText || this.name || '';
    const suffix = timeStr ? `　${timeStr}発` : '';
    return `${this.title}：${name}${suffix}`;
  }

  // 航空券の予約番号または確認番号が未入力かどうか
  get isAirNumbersMissing(): boolean {
    return !this.airReservationNumber || !this.airConfirmationNumber;
  }

  get description(): string {
    return `<${this.title}>
■ご利用：${this.peopleNum}名
■内容
${this.summary}`;
  }

  get isPackage() {
    return [2, 3].includes(this.airTicketType || 0);
  }

  get marketLogType() {
    if (this.transportType === 'foreign_air') return this.type;
    return this.transportType;
  }

  detail(orderItem: OrderItem | null = null, opts: ElementDetailOptions = {}) {
    return this.structuredDetail(orderItem, opts)
      .map(line => `${line.label}${line.value}\n`)
      .join('');
  }

  validationErrors(orderItemStatus?: number): string[] {
    const errors: string[] = [];
    const needStatusGuard = orderItemStatus === 7 || orderItemStatus === 13;

    switch (this.transportType) {
      case 'domestic_air':
        if (!this.name || this.name === '') {
          errors.push('便名を入力してください。\n予約が不要な場合、\n航空券の内容を削除してください。');
        }
        if (this.ticketingType === undefined || this.ticketingType === null) {
          errors.push('発券方法を選択してください。');
        }
        if (needStatusGuard && (!this.airReservationNumber || this.airReservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        if (needStatusGuard) {
          const isAnaAfterMay19 =
            !!this.name?.includes('ANA') &&
            !!this.from?.time &&
            this.from.time.clone().tz(this.from.timeZone).isSameOrAfter(moment.tz('2026-05-19', 'Asia/Tokyo'));
          if (isAnaAfterMay19) {
            if (!this.ticketNumber || this.ticketNumber === '') {
              errors.push('航空券番号を入力してください。');
            }
          } else if (!this.airConfirmationNumber || this.airConfirmationNumber === '') {
            errors.push('確認番号を入力してください。');
          }
        }
        break;
      case 'foreign_air':
        if (!this.name || this.name === '') {
          errors.push('便名を入力してください。\n予約が不要な場合、\n航空券の内容を削除してください。');
        }
        // foreign_airの場合はreservationNumberをチェック
        if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        break;
      case 'ana_tabisaku':
        if (!this.name || this.name === '') {
          errors.push('便名を入力してください。\n予約が不要な場合、\n航空券の内容を削除してください。');
        }
        break;
      case 'shinkansen':
        if (
          !this.boardingStation ||
          this.boardingStation === '' ||
          !this.arrivalStation ||
          this.arrivalStation === ''
        ) {
          errors.push(
            '出発・到着の場所を入力してください。\n予約が不要な場合、\n新幹線の内容を削除してください。'
          );
        }
        if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        break;
      case 'express':
        if (
          !this.boardingStation ||
          this.boardingStation === '' ||
          !this.arrivalStation ||
          this.arrivalStation === ''
        ) {
          errors.push('出発・到着の場所を入力してください。\n予約が不要な場合、\n特急の内容を削除してください。');
        }
        if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        break;
      case 'railway_ticket':
        if (
          !this.boardingStation ||
          this.boardingStation === '' ||
          !this.arrivalStation ||
          this.arrivalStation === ''
        ) {
          errors.push(
            '出発・到着の場所を入力してください。\n予約が不要な場合、\n在来線の内容を削除してください。'
          );
        }
        if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        break;
      case 'foreign_railway':
        if (
          !this.boardingStation ||
          this.boardingStation === '' ||
          !this.arrivalStation ||
          this.arrivalStation === ''
        ) {
          errors.push(
            '出発・到着の場所を入力してください。\n予約が不要な場合、\n海外鉄道の内容を削除してください。'
          );
        }
        if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        break;
      case 'rental_car':
        if (!this.name || this.name === '') {
          errors.push('便名を入力してください。\n予約が不要な場合、\nレンタカーの内容を削除してください。');
        }
        if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        break;
      case 'taxi':
        if (!this.name || this.name === '') {
          errors.push('便名を入力してください。\n予約が不要な場合、\nタクシーの内容を削除してください。');
        }
        if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        break;
      case 'bus':
        if (!this.name || this.name === '') {
          errors.push('便名を入力してください。\n予約が不要な場合、\nバスの内容を削除してください。');
        }
        if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
          errors.push('予約番号を入力してください。');
        }
        break;
      default:
        break;
    }

    // 時間の検証: to.time < from.time
    if (this.to && this.from && this.to.time && this.from.time && this.to.time < this.from.time) {
      errors.push('出発以降を指定してください。');
    }

    return errors;
  }

  get updateParams() {
    return {
      id: this.id,
      type: this.type,
      transport_type: this.transportType,
      air_ticket_type: this.airTicketType,
      ticketing_type: this.ticketingType,
      name: this.name,
      people_num: this.peopleNum,
      railway_seat_type: this.railwayTicketType !== 2 ? this.railwaySeatType : null,
      railway_ticket_type: this.railwayTicketType,
      from: this.from.updateParams,
      to: this.to.updateParams,
      boarding_station: this.boardingStation,
      arrival_station: this.arrivalStation,
      posting_number: this.postingNumber ?? null,
      exic_receipt_number: this.exicReceiptNumber ?? null,
      auto_reservation_status: this.autoReservationStatus ?? null,
      sold_out: this.soldOut,
      airline_code: this.airlineCode ?? null,
      air_reservation_number: this.airReservationNumber ?? null,
      air_confirmation_number: this.airConfirmationNumber ?? null,
      air_seat_number: this.airSeatNumber ?? null,
      sakura_ticket_type: this.sakuraTicketType,
      air_seat_type: this.airSeatType,
      last_name_kana: this.lastNameKana,
      first_name_kana: this.firstNameKana,
      driver_tel: this.driverTel,
      car_type: this.carType,
      car_type_other: this.carTypeOther,
      remarks: this.remarks,
      car_number: this.carNumber,
      car_provider: this.carProvider,
      taxi_service: this.taxiService,
      ticketing_expired_at: this.ticketingExpiredAt || null,
      allies_application_number: this.alliesApplicationNumber,
      frontier_application_number: this.frontierApplicationNumber,
      skytours_application_number: this.skytoursApplicationNumber,
      ticketing_at: this.ticketingAt ? this.ticketingAt.format('YYYY-MM-DD') : undefined,
      reservation_number: this.reservationNumber ?? null,
      ticket_number: this.ticketNumber ?? null,
      cabin: this.cabin ?? null,
      transport_element_attach_file: this.transportElementAttachFiles?.map(file => file.updateParams),
      upload_file_data: this.uploadFileData || {}
    };
  }

  get isTicketingExpired() {
    return this.ticketingExpiredAt ? this.ticketingExpiredAt.isBefore(moment()) : false;
  }

  get startTime() {
    return this.from.time;
  }

  get endTime() {
    return this.to.time;
  }

  getSeatLabel() {
    return this.transportType === TransportElement.TRANSPORT_TYPES.FOREIGN_AIR ? '座席の種別:' : '';
  }

  structuredDetail(orderItem: OrderItem | null = null, opts: ElementDetailOptions = {}) {
    const lines = [];
    if (this.ticketingExpiredAt) {
      lines.push({
        label: '',
        value: `発券期限:${this.ticketingExpiredAt.format('YYYY年MM月DD日HH時mm分')}`,
        translate: true
      });
    }
    if (!this.isRailway) {
      lines.push({
        label: '',
        value: '※表記は現地時刻です。',
        translate: true
      });
    }

    if (orderItem) {
      const seatType = this.travelerSeatTypeStr(orderItem, opts.elementIndex);
      if (seatType) {
        lines.push({
          label: '',
          value: seatType,
          translate: true
        });
      }

      const ticketType = this.travelerTicketTypeStr(orderItem, opts.elementIndex);
      if (ticketType) {
        lines.push({
          label: '',
          value: ticketType,
          translate: true
        });
      }
    }

    if (this.transportType === 'domestic_air' || this.transportType === 'foreign_air') {
      const airTicketType = this.airChangeableDescriptionWithFare || this.airTicketTypeStr;
      if (airTicketType) {
        lines.push({
          label: '',
          value: airTicketType,
          translate: true
        });
      }

      if (this.airSeatType) {
        lines.push({
          label: this.getSeatLabel(),
          value: this.airSeatType,
          translate: true
        });
      }
    }

    if (this.isRailway) {
      if (this.boardingStation) {
        lines.push({
          label: '乗車駅:',
          value: this.boardingStation,
          translate: false
        });
      }
      if (this.arrivalStation) {
        lines.push({
          label: '降車駅:',
          value: this.arrivalStation,
          translate: false
        });
      }
    }

    if (orderItem?.isJrPackage) {
      if (orderItem.jrAuthCode) {
        lines.push({
          label: 'JR認証番号:',
          value: orderItem.jrAuthCode,
          translate: false
        });
      }
      if (orderItem.jrPickupCode) {
        lines.push({
          label: 'JR引取番号:',
          value: orderItem.jrPickupCode,
          translate: false
        });
      }
    }

    return lines;
  }

  travelerSeatTypeStr(orderItem: OrderItem, elementIndex: number | null = null) {
    if (!this.isRailway) {
      return '';
    }

    const seatType = this.railwaySeatTypeStr();
    if (!_.isEmpty(seatType)) {
      return seatType;
    }

    if (!orderItem) {
      return '';
    }

    const seatTypes = Array.from(new Set(orderItem.travelerShinkansenSeatTypes(elementIndex)));
    return seatTypes.join('、');
  }

  travelerTicketTypeStr(orderItem: OrderItem, elementIndex: number | null = null) {
    if (!this.isRailway) {
      return '';
    }

    const ticketType = this.railwayTicketTypeStr();
    if (!_.isEmpty(ticketType)) {
      return ticketType;
    }

    if (!orderItem) {
      return '';
    }

    const ticketTypes = Array.from(new Set(orderItem.travelerShinkansenTicketTypes(elementIndex)));
    return ticketTypes.join('、');
  }

  railwaySeatTypeStr() {
    if (!this.isRailway) {
      return '';
    }
    switch (this.railwaySeatType) {
      case 0:
        return '自由席';
      case 1:
        return '指定席';
      case 2:
        return 'グリーン車';
      case 3:
        return '回数券';
      case 4:
        return 'EX-IC';
      case 5:
        return 'グランクラス';
      default:
        return '';
    }
  }

  railwayTicketTypeStr() {
    if (!this.isRailway) {
      return '';
    }
    switch (this.railwayTicketType) {
      case 0:
        return '通常きっぷ';
      case 1:
        return 'JR九州きっぷ';
      case 2:
        return '回数券';
      case 3:
        return 'EX-IC';
      default:
        return '';
    }
  }

  handleUploadFileData(
    uploadFileData: UploadFileData,
    transportElementId: number | undefined,
    travelerId: string | null
  ): void {
    if (!this.uploadFileData) {
      this.uploadFileData = {};
    }
    const elementKey = transportElementId ?? this.id ?? 0;
    if (!this.uploadFileData[elementKey]) {
      this.uploadFileData[elementKey] = {};
    }
    const travelerKey = (travelerId ?? 'null').toString();
    if (!this.uploadFileData[elementKey][travelerKey]) {
      this.uploadFileData[elementKey][travelerKey] = [];
    }
    this.uploadFileData[elementKey][travelerKey].push({
      ...uploadFileData,
      traveler_information_id: uploadFileData.traveler_information_id ?? travelerId,
      transport_element_id: uploadFileData.transport_element_id ?? elementKey
    });
  }

  handleDeleteFile(transportElementId: number | undefined, travelerId: string | null, index: number): void {
    if (!this.uploadFileData) {
      return;
    }

    const elementKey = transportElementId ?? this.id ?? 0;
    const travelerKey = (travelerId ?? 'null').toString();
    if (
      this.uploadFileData[elementKey] &&
      this.uploadFileData[elementKey][travelerKey] &&
      this.uploadFileData[elementKey][travelerKey].length > index
    ) {
      this.uploadFileData[elementKey][travelerKey].splice(index, 1);

      if (this.uploadFileData[elementKey][travelerKey].length === 0) {
        delete this.uploadFileData[elementKey][travelerKey];
      }

      if (Object.keys(this.uploadFileData[elementKey]).length === 0) {
        delete this.uploadFileData[elementKey];
      }
    }
  }

  private buildUploadFileDataFromAttachFiles(
    attachFiles?: TransportElementAttachFile[]
  ): Record<number, Record<string, UploadFileData[]>> | undefined {
    if (!attachFiles || attachFiles.length === 0) {
      return undefined;
    }
    const data = attachFiles.reduce<Record<number, Record<string, UploadFileData[]>>>((acc, file) => {
      const elementId = file.transportElementId ?? this.id ?? 0;
      const travelerKey = (file.travelerInformationId ?? 'null').toString();
      acc[elementId] = acc[elementId] ?? {};
      acc[elementId][travelerKey] = acc[elementId][travelerKey] ?? [];
      acc[elementId][travelerKey].push({
        upload_file_name: file.uploadFileName,
        content_type: file.contentType,
        upload_file_base64: '',
        traveler_information_id: file.travelerInformationId,
        id: file.id,
        trip_id: file.tripId,
        transport_element_id: elementId,
        file_path: file.filePath
      });
      return acc;
    }, {});
    return Object.keys(data).length > 0 ? data : undefined;
  }

  handleTicketingTypeChangeWithSuppliedId(
    value: number,
    orderItem: OrderItem,
    suppliedItems: SuppliedItem[],
    individualDomesticAirSuppliedItems: IndividualDomesticAirSuppliedItem[]
  ): void {
    this.ticketingType = value;
    const ticketingType = value;
    const name = this.name || '';

    // Update original prices with individual supplied item ID
    if (orderItem.price?.originalPrices) {
      orderItem.price.originalPrices.forEach(originalPrice => {
        originalPrice.individualSuppliedItemId(
          name,
          ticketingType,
          suppliedItems,
          individualDomesticAirSuppliedItems
        );
      });
    }
  }

  // フライト時間を計算して表示
  get flightTime(): string {
    const fromTime = this.from.time.clone();
    const toTime = this.to.time.clone();

    // 時差を考慮した時間差を計算
    const diffMs = toTime.diff(fromTime);
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs - hours * 3600000) / 60000);

    // 時間と分を2桁でフォーマット
    const formattedHours = String(hours + 100).substring(1);
    const formattedMinutes = String(minutes + 100).substring(1);

    return `搭乗時間 : ${formattedHours}時間${formattedMinutes}分`;
  }

  get isChangeable(): boolean {
    return this.isDomesticAir && this.airTicketType === 0;
  }

  isRawTicketingExpired(orderItem: OrderItem) {
    if (!this.isForeignAir) return false;

    if (!orderItem || !orderItem.flight) return false;

    const dateStr =
      orderItem.flight.homeword?.rawTicketingExpiredAt || orderItem.flight.outword?.rawTicketingExpiredAt;
    return !!dateStr;
  }

  rawTicketingExpiredAt(orderItem: OrderItem): string | null {
    if (!this.isForeignAir || !orderItem?.flight) {
      return null;
    }

    const dates: moment.Moment[] = [];

    if (orderItem.flight.homeword?.rawTicketingExpiredAt) {
      dates.push(moment(orderItem.flight.homeword.rawTicketingExpiredAt));
    }

    if (orderItem.flight.outword?.rawTicketingExpiredAt) {
      dates.push(moment(orderItem.flight.outword.rawTicketingExpiredAt));
    }

    if (dates.length === 0) {
      return null;
    }

    return moment.min(dates).format('YYYY年MM月DD日HH時mm分');
  }
}
